/**
 * Global Tier-Based Pricing Calculations
 * Calculates pricing based on unit types (fioles, boîtes) not individual products
 */

export interface GlobalTier {
    tier_number: number;
    min_quantity: number;
    discount_percentage?: number | null;
    free_units?: number | null;
    client_price: number;
    triggered: boolean;
}

function normalizeKeyPart(value?: string | null): string {
    if (!value) {
        return '';
    }
    return value.trim().toLowerCase();
}

export function buildTierGroupKey(
    marque?: string | null,
    gamme?: string | null,
    unitName?: string | null
): string {
    return [normalizeKeyPart(marque), normalizeKeyPart(gamme), normalizeKeyPart(unitName)].join('|');
}

export interface UnitTypeTierData {
    unit_name: string;
    marque?: string;
    gamme?: string;
    volume: number;
    catalog_price: number;
    total_lots_ordered: number;
    tiers: GlobalTier[];
    tier_mode: 'ug' | 'percent';
}

export interface TierBreakdownResponse {
    tier_breakdown: Record<string, UnitTypeTierData>;
    has_global_tiers: boolean;
}

interface GlobalTierPayload {
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
    unit?: string | null;
    volume?: number | null;
    marque?: string | null;
    gamme?: string | null;
}

export interface CartItem {
    consumable: {
        id: number;
        title: string;
        base_price: number;
        custom_price?: number;
        batch_size: number | null;
        unit?: string; // Unit field from backend (e.g., "fiole", "boîte")
        volume_ml?: number; // Volume in ml for tier matching
        marque?: string | null;
        gamme?: string | null;
        tiers?: GlobalTierPayload[];
    };
    quantity: number; // in lots
}

export interface PricingResult {
    unit_price: number;
    total_cost: number;
    free_units: number;
    total_received: number;
    tier_triggered: boolean;
    tier_number?: number;
    display_text: string;
}

/**
 * Extract unit name from consumable
 * Prefers the 'unit' field, falls back to parsing title
 * Example: unit="fiole" -> "fiole"
 */
export function extractUnitName(title: string, unit?: string): string | null {
    // Prefer the unit field if available
    if (unit) {
        const unitName = unit.toLowerCase();
        return unitName;
    }

    // Fallback to title parsing
    const parts = title.split('|');
    if (parts.length >= 3) {
        const unitPart = parts[parts.length - 1].trim();
        const words = unitPart.split(' ');
        if (words.length > 0) {
            const unitName = words[0].toLowerCase();
            return unitName;
        }
    }
    return null;
}

/**
 * Group cart items by unit type and volume_ml
 */
export function groupCartByUnitType(cart: CartItem[]): Map<string, { items: CartItem[], totalLots: number, batchSize: number, unitName: string, volume_ml?: number; marque?: string | null; gamme?: string | null }> {
    const groups = new Map();

    for (const item of cart) {
        const unitName = extractUnitName(item.consumable.title, item.consumable.unit);
        if (!unitName) continue;

        const batchSize = item.consumable.batch_size || 1;
        const volume_ml = item.consumable.volume_ml;
        const key = buildTierGroupKey(item.consumable.marque, item.consumable.gamme, unitName);

        if (!groups.has(key)) {
            groups.set(key, {
                items: [],
                totalLots: 0,
                batchSize,
                unitName,
                volume_ml,
                marque: item.consumable.marque,
                gamme: item.consumable.gamme
            });
        }

        const group = groups.get(key);
        group.items.push(item);
        group.totalLots += item.quantity;
    }

    return groups;
}

/**
 * Calculate pricing for a single item using global tier data
 */
export function calculateItemPricingWithGlobalTiers(
    item: CartItem,
    tierBreakdown: TierBreakdownResponse
): PricingResult {
    const unitName = extractUnitName(item.consumable.title, item.consumable.unit);
    const batchSize = item.consumable.batch_size || 1;
    const baseUnitPrice: number = item.consumable.custom_price ?? item.consumable.base_price;

    if (!unitName || !tierBreakdown.has_global_tiers) {
        // No global tiers - use base price
        const totalCost = item.quantity * baseUnitPrice;
        return {
            unit_price: baseUnitPrice,
            total_cost: totalCost,
            free_units: 0,
            total_received: item.quantity * batchSize,
            tier_triggered: false,
            display_text: `${item.quantity} lot${item.quantity > 1 ? 's' : ''}`
        };
    }

    // Use same key format as groupCartByUnitType
    const key = buildTierGroupKey(item.consumable.marque, item.consumable.gamme, unitName);
    const unitTierData = tierBreakdown.tier_breakdown[key];

    if (!unitTierData) {
        // No tiers for this unit type - use base price
        const totalUnits = item.quantity * batchSize;
        const totalCost = totalUnits * baseUnitPrice;
        return {
            unit_price: baseUnitPrice,
            total_cost: totalCost,
            free_units: 0,
            total_received: totalUnits,
            tier_triggered: false,
            display_text: `${item.quantity} lot${item.quantity > 1 ? 's' : ''}`
        };
    }

    // Find triggered tier
    const triggeredTier = unitTierData.tiers.find(t => t.triggered);

    if (!triggeredTier) {
        // No tier triggered - use product price
        const totalUnits = item.quantity * batchSize;
        const totalCost = totalUnits * baseUnitPrice;
        return {
            unit_price: baseUnitPrice,
            total_cost: totalCost,
            free_units: 0,
            total_received: totalUnits,
            tier_triggered: false,
            display_text: `${item.quantity} lot${item.quantity > 1 ? 's' : ''}`
        };
    }

    // Apply triggered tier - price stays the same, only bonuses change
    const pricePerUnit: number = baseUnitPrice;
    const totalUnits = item.quantity * batchSize;
    const baseTotalCost = totalUnits * pricePerUnit;

    if (unitTierData.tier_mode === 'ug' && triggeredTier.free_units) {
        // UG mode - free_units is the total bonus for reaching this tier
        const totalFreeUnits = triggeredTier.free_units;
        const totalUnitsOrdered = totalUnits;
        const totalReceived = totalUnitsOrdered;

        // Calculate effective price per unit (what you actually pay per unit including free ones)
        // Formula: Prix Client CHT = Prix Catalogue CHT × Quantity (units) / Total Received
        const effectivepricePerUnit = (pricePerUnit * totalUnitsOrdered) / (totalUnitsOrdered + totalFreeUnits);

        // Total cost = Effective Price Per Unit × Total Units Ordered
        const totalCost = effectivepricePerUnit * totalUnitsOrdered;

        return {
            unit_price: effectivepricePerUnit,
            total_cost: totalCost,
            free_units: totalFreeUnits,
            total_received: totalReceived,
            tier_triggered: true,
            tier_number: triggeredTier.tier_number,
            display_text: `${totalUnitsOrdered} + ${totalFreeUnits} gratuit`
        };
    } else {
        // Percentage mode - apply discount to total cost
        const discountPercent = triggeredTier.discount_percentage || 0;
        const discountedUnitPrice = pricePerUnit * (1 - discountPercent / 100);
        const discountedTotalCost = baseTotalCost * (1 - discountPercent / 100);

        return {
            unit_price: discountedUnitPrice,
            total_cost: discountedTotalCost,
            free_units: 0,
            total_received: totalUnits,
            tier_triggered: true,
            tier_number: triggeredTier.tier_number,
            display_text: `${item.quantity} lot${item.quantity > 1 ? 's' : ''} (-${discountPercent}%)`
        };
    }
}

/**
 * Calculate total for entire cart using global tiers
 */
export function calculateCartTotalWithGlobalTiers(
    cart: CartItem[],
    tierBreakdown: TierBreakdownResponse
): number {
    return cart.reduce((total, item) => {
        const pricing = calculateItemPricingWithGlobalTiers(item, tierBreakdown);
        return total + pricing.total_cost;
    }, 0);
}

/**
 * Fetch global tiers for admin context using client_id
 * Uses /client-pricing endpoint with client_id parameter
 */
export async function fetchGlobalTiersForAdmin(
    api: any,
    clientId: number,
    cart: CartItem[]
): Promise<TierBreakdownResponse> {
    try {
        // Admin endpoint: get pricing data with client_id parameter
        const pricingResponse = await api.get(`/client-pricing?client_id=${clientId}`);
        const bpus = Array.isArray(pricingResponse.data) ? pricingResponse.data : [];

        // Get active BPU's pricing_items
        const activeBpu = bpus.find((bpu: any) => bpu.status === 'validated' && bpu.is_active);
        const products = activeBpu?.pricing_items || [];

        if (products.length === 0) {
            return { tier_breakdown: {}, has_global_tiers: false };
        }

        // Extract global tiers from products (consumable is nested in admin response)
        const allGlobalTiers: GlobalTierPayload[] = [];
        const tierKeys: Set<string> = new Set();
        products.forEach((product: { tiers?: GlobalTierPayload[]; consumable?: { unit?: string; volume_ml?: number | null; marque?: string | null; gamme?: string | null } }) => {
            const tiers = Array.isArray(product.tiers) ? product.tiers : [];
            tiers.forEach((tier: GlobalTierPayload) => {
                const unit = product.consumable?.unit ?? '';
                const volume = product.consumable?.volume_ml ?? '';
                const marque = product.consumable?.marque ?? '';
                const gamme = product.consumable?.gamme ?? '';
                const tierKey = [
                    unit,
                    volume,
                    marque,
                    gamme,
                    tier.tier_number,
                    tier.min_quantity,
                    tier.max_quantity ?? 'null',
                    tier.discount_percentage ?? 'null',
                    tier.free_units ?? 'null'
                ].join('|');
                if (tierKeys.has(tierKey)) {
                    return;
                }
                tierKeys.add(tierKey);
                allGlobalTiers.push({
                    ...tier,
                    unit: unit || undefined,
                    volume: product.consumable?.volume_ml ?? undefined,
                    marque: product.consumable?.marque ?? undefined,
                    gamme: product.consumable?.gamme ?? undefined
                });
            });
        });

        if (allGlobalTiers.length === 0) {
            return { tier_breakdown: {}, has_global_tiers: false };
        }

        // Group cart by unit type to calculate total lots
        const unitGroups = groupCartByUnitType(cart);

        // Build tier breakdown (shared logic)
        return buildTierBreakdownFromGroups(unitGroups, allGlobalTiers);
    } catch (error) {
        console.error('[ADMIN GLOBAL TIER] Error fetching tiers:', error);
        return { tier_breakdown: {}, has_global_tiers: false };
    }
}

/**
 * Build tier breakdown from unit groups and global tiers
 * Shared helper function for both admin and client contexts
 */
function buildTierBreakdownFromGroups(
    unitGroups: Map<string, { items: CartItem[], totalLots: number, batchSize: number, unitName: string, volume_ml?: number; marque?: string | null; gamme?: string | null }>,
    allGlobalTiers: GlobalTierPayload[]
): TierBreakdownResponse {
    const tierBreakdown: Record<string, UnitTypeTierData> = {};

    for (const [key, group] of unitGroups.entries()) {
        const { unitName, batchSize, totalLots, marque, gamme } = group;

        // Find matching global tiers for this unit type and volume
        const matchingTiers: GlobalTierPayload[] = allGlobalTiers.filter(
            (tier: GlobalTierPayload) => {
                const tierUnitName = tier.unit ? tier.unit.toLowerCase() : '';
                const tierUnitPlural = tierUnitName.endsWith('s') ? tierUnitName : `${tierUnitName}s`;
                const marqueMatch = normalizeKeyPart(tier.marque) === normalizeKeyPart(marque);
                const gammeMatch = normalizeKeyPart(tier.gamme) === normalizeKeyPart(gamme);
                return tierUnitPlural === unitName.toLowerCase() && marqueMatch && gammeMatch;
            }
        );

        if (matchingTiers.length === 0) continue;

        // Get catalog price from first item in group
        const catalogPrice: number = group.items[0]?.consumable.custom_price
            ?? group.items[0]?.consumable.base_price
            ?? 0;

        // Determine triggered tier
        const sortedTiers: GlobalTierPayload[] = [...matchingTiers].sort(
            (a: GlobalTierPayload, b: GlobalTierPayload) => a.min_quantity - b.min_quantity
        );
        let triggeredTier: GlobalTierPayload | null = null;

        for (const tier of sortedTiers) {
            if (totalLots >= tier.min_quantity) {
                triggeredTier = tier;
            } else {
                break;
            }
        }

        // Build tier list
        const tiers: GlobalTier[] = sortedTiers.map((tier: GlobalTierPayload) => {
            const isTriggered = Boolean(
                triggeredTier
                && tier.tier_number === triggeredTier.tier_number
                && tier.min_quantity === triggeredTier.min_quantity
            );

            // Calculate client price
            let clientPrice = catalogPrice;
            if (tier.discount_percentage !== null && tier.discount_percentage !== undefined) {
                clientPrice = catalogPrice * (1 - tier.discount_percentage / 100);
            } else if (tier.free_units !== null && tier.free_units !== undefined) {
                const effectiveUnits = batchSize + tier.free_units;
                clientPrice = catalogPrice * (batchSize / effectiveUnits);
            }

            return {
                tier_number: tier.tier_number,
                min_quantity: tier.min_quantity,
                discount_percentage: tier.discount_percentage,
                free_units: tier.free_units,
                client_price: clientPrice,
                triggered: isTriggered
            };
        });

        const tierMode: 'ug' | 'percent' = sortedTiers[0].free_units !== null ? 'ug' : 'percent';

        tierBreakdown[key] = {
            unit_name: unitName.charAt(0).toUpperCase() + unitName.slice(1),
            marque: marque ?? undefined,
            gamme: gamme ?? undefined,
            volume: batchSize,
            catalog_price: catalogPrice,
            total_lots_ordered: totalLots,
            tiers,
            tier_mode: tierMode as 'ug' | 'percent'
        };
    }

    return {
        tier_breakdown: tierBreakdown,
        has_global_tiers: Object.keys(tierBreakdown).length > 0
    };
}

/**
 * Fetch global tiers for client portal context
 * Uses /clients/me/pricing endpoint
 */
export async function fetchGlobalTiersForClient(
    api: any,
    _clientId: number,
    cart: CartItem[]
): Promise<TierBreakdownResponse> {
    try {
        // Client portal endpoint: get pricing data for authenticated client
        const pricingResponse = await api.get('/clients/me/pricing');
        const products = Array.isArray(pricingResponse.data) ? pricingResponse.data : [];

        // Extract global tiers from cart items first (preferred), fallback to pricing response
        const allGlobalTiers: GlobalTierPayload[] = [];
        const tierKeys: Set<string> = new Set();
        const cartTierSources = cart
            .filter(item => (item.consumable.tiers ?? []).length > 0)
            .map(item => ({
                tiers: item.consumable.tiers ?? [],
                unit: item.consumable.unit,
                volume_ml: item.consumable.volume_ml ?? undefined,
                marque: item.consumable.marque,
                gamme: item.consumable.gamme
            }));
        const tierSources = cartTierSources.length > 0 ? cartTierSources : products;

        if (tierSources.length === 0) {
            return { tier_breakdown: {}, has_global_tiers: false };
        }

        tierSources.forEach((product: { tiers?: GlobalTierPayload[]; unit?: string; volume_ml?: number | null; marque?: string | null; gamme?: string | null }) => {
            const tiers = Array.isArray(product.tiers) ? product.tiers : [];
            tiers.forEach((tier: GlobalTierPayload) => {
                const unit = product.unit ?? '';
                const volume = product.volume_ml ?? '';
                const marque = product.marque ?? '';
                const gamme = product.gamme ?? '';
                const tierKey = [
                    unit,
                    volume,
                    marque,
                    gamme,
                    tier.tier_number,
                    tier.min_quantity,
                    tier.max_quantity ?? 'null',
                    tier.discount_percentage ?? 'null',
                    tier.free_units ?? 'null'
                ].join('|');
                if (tierKeys.has(tierKey)) {
                    return;
                }
                tierKeys.add(tierKey);
                allGlobalTiers.push({
                    ...tier,
                    unit: unit || undefined,
                    volume: product.volume_ml ?? undefined,
                    marque: product.marque ?? undefined,
                    gamme: product.gamme ?? undefined
                });
            });
        });

        if (allGlobalTiers.length === 0) {
            return { tier_breakdown: {}, has_global_tiers: false };
        }

        // Group cart by unit type to calculate total lots
        const unitGroups = groupCartByUnitType(cart);

        // Build tier breakdown (shared logic)
        return buildTierBreakdownFromGroups(unitGroups, allGlobalTiers);
    } catch (error) {
        console.error('[CLIENT GLOBAL TIER] Error fetching tiers:', error);
        return { tier_breakdown: {}, has_global_tiers: false };
    }
}
